import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Search, Filter, Truck, Star, Upload, Edit, Trash2, ChevronLeft, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export default function AdminVehiculos() {
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ familia: '', subfamilia: '', operativa: '', favoritos: false, search: '' });
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editForm, setEditForm] = useState({
    operativa: true,
    favorito: false,
    capacidad_m3: '',
    capacidad_kg: '',
    capacidad_tn: '',
    notes: ''
  });
  
  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchVehicles = async (pg = 1, flt = filters) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: 20 };
      if (flt.familia && flt.familia.trim()) params.familia = flt.familia;
      if (flt.subfamilia && flt.subfamilia.trim()) params.subfamilia = flt.subfamilia;
      if (flt.operativa && flt.operativa.trim()) params.operativa = flt.operativa === 'true';
      if (flt.favoritos) params.favoritos = true;
      if (flt.search) params.search = flt.search;
      const resp = await axios.get(`${API}/admin/vehicles`, { headers: authHeaders(), params });
      setVehicles(resp.data.vehicles);
      setTotal(resp.data.total);
      setPages(resp.data.pages);
      setPage(pg);
    } catch (err) {
      toast.error('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVehicles(1); }, []);

  const applyFilters = () => fetchVehicles(1, filters);
  
  const openEditModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditForm({
      operativa: vehicle.operativa ?? true,
      favorito: vehicle.favorito ?? false,
      capacidad_m3: vehicle.capacidad_m3 || '',
      capacidad_kg: vehicle.capacidad_kg || '',
      capacidad_tn: vehicle.capacidad_tn || '',
      notes: vehicle.notes || ''
    });
    setEditModalOpen(true);
  };

  const handleUpdateVehicle = async () => {
    if (!selectedVehicle) return;
    try {
      const payload = {
        operativa: editForm.operativa,
        favorito: editForm.favorito,
        capacidad_m3: editForm.capacidad_m3 ? parseFloat(editForm.capacidad_m3) : null,
        capacidad_kg: editForm.capacidad_kg ? parseFloat(editForm.capacidad_kg) : null,
        capacidad_tn: editForm.capacidad_tn ? parseFloat(editForm.capacidad_tn) : null,
        notes: editForm.notes || null
      };
      await axios.patch(`${API}/admin/vehicles/${selectedVehicle.id}`, payload, { headers: authHeaders() });
      toast.success('Vehículo actualizado correctamente');
      setEditModalOpen(false);
      fetchVehicles(page, filters);
    } catch (err) {
      toast.error('Error al actualizar vehículo');
    }
  };

  const handleDeleteVehicle = async (vehicle) => {
    if (!window.confirm(`¿Eliminar vehículo ${vehicle.matricula}?`)) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${vehicle.id}`, { headers: authHeaders() });
      toast.success('Vehículo eliminado');
      fetchVehicles(page, filters);
    } catch (err) {
      toast.error('Error al eliminar vehículo');
    }
  };

  const handleToggleFavorito = async (vehicle) => {
    try {
      await axios.patch(`${API}/admin/vehicles/${vehicle.id}`, { favorito: !vehicle.favorito }, { headers: authHeaders() });
      toast.success(vehicle.favorito ? 'Quitado de favoritos' : 'Añadido a favoritos');
      fetchVehicles(page, filters);
    } catch (err) {
      toast.error('Error al actualizar favorito');
    }
  };

  // Import preview (dry-run)
  const handleImportPreview = async () => {
    if (!importFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('mode', 'dry_run');
      const resp = await axios.post(`${API}/admin/vehicles/import?mode=dry_run`, formData, { headers: authHeaders() });
      setImportPreview(resp.data.preview);
      toast.success('Vista previa generada');
    } catch (err) {
      toast.error('Error al procesar archivo');
    } finally {
      setImporting(false);
    }
  };

  // Import commit
  const handleImportCommit = async (mode) => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const resp = await axios.post(`${API}/admin/vehicles/import?mode=${mode}`, formData, { headers: authHeaders() });
      toast.success(`Importación completada: ${resp.data.result.created} creados, ${resp.data.result.updated} actualizados, ${resp.data.result.skipped} omitidos`);
      setImportModalOpen(false);
      setImportFile(null);
      setImportPreview(null);
      fetchVehicles(1, filters);
    } catch (err) {
      toast.error('Error al importar vehículos');
    } finally {
      setImporting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67]">Vehículos</h1>
            <p className="text-sm text-slate-500">{total} total</p>
          </div>
          <Button onClick={() => setImportModalOpen(true)} className="bg-[#009940] hover:bg-[#007F35]" data-testid="vehicles-import-button">
            <Upload size={16} className="mr-2" />
            Importar Excel
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1">Buscar</Label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Matrícula, marca, modelo..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                  className="pl-9"
                  data-testid="vehicles-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1">Familia</Label>
              <Select value={filters.familia} onValueChange={(val) => setFilters({ ...filters, familia: val })}>
                <SelectTrigger data-testid="vehicles-filter-familia">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todas</SelectItem>
                  <SelectItem value="VEHICULOS">Vehículos</SelectItem>
                  <SelectItem value="CRC">CRC</SelectItem>
                  <SelectItem value="FURGONES CERRADOS">Furgones Cerrados</SelectItem>
                  <SelectItem value="CAJA ABIERTA HASTA 3500 KG">Caja Abierta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1">Operativa</Label>
              <Select value={filters.operativa} onValueChange={(val) => setFilters({ ...filters, operativa: val })}>
                <SelectTrigger data-testid="vehicles-filter-operativa">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todas</SelectItem>
                  <SelectItem value="true">Sí</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.favoritos}
                  onChange={(e) => setFilters({ ...filters, favoritos: e.target.checked })}
                  className="rounded border-slate-300"
                  data-testid="vehicles-filter-favoritos"
                />
                <span className="text-sm text-slate-700">Solo favoritos</span>
              </label>
            </div>
          </div>
          <Button onClick={applyFilters} className="w-full md:w-auto" data-testid="vehicles-apply-filters-button">
            <Filter size={16} className="mr-2" />
            Aplicar Filtros
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando vehículos...</div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-12 text-slate-500">No hay vehículos</div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Matrícula</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Marca/Modelo</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Familia</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Combustible</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Capacidad</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Estado</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v) => (
                      <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`vehicle-row-${v.matricula}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-slate-900">{v.matricula}</span>
                            {v.favorito && <Star size={14} className="text-amber-500 fill-amber-500" />}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {v.marca} {v.modelo}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className="text-xs">{v.familia || 'N/A'}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{v.combustible || 'N/A'}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs">
                          {v.capacidad_m3 ? `${v.capacidad_m3} m³` : v.capacidad_kg ? `${v.capacidad_kg} kg` : v.capacidad_tn ? `${v.capacidad_tn} tn` : 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          {v.operativa ? (
                            <div className="flex items-center gap-1 text-green-700">
                              <CheckCircle2 size={14} />
                              <span className="text-xs">Operativa</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-600">
                              <XCircle size={14} />
                              <span className="text-xs">No operativa</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleToggleFavorito(v)}
                              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                              title={v.favorito ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                              data-testid={`vehicle-toggle-favorite-${v.matricula}`}
                            >
                              <Star size={16} className={v.favorito ? 'text-amber-500 fill-amber-500' : 'text-slate-400'} />
                            </button>
                            <button
                              onClick={() => openEditModal(v)}
                              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                              title="Editar"
                              data-testid={`vehicle-edit-${v.matricula}`}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteVehicle(v)}
                              className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                              title="Eliminar"
                              data-testid={`vehicle-delete-${v.matricula}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Página {page} de {pages}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchVehicles(page - 1, filters)}
                    disabled={page === 1}
                    data-testid="vehicles-prev-page"
                  >
                    <ChevronLeft size={16} />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchVehicles(page + 1, filters)}
                    disabled={page === pages}
                    data-testid="vehicles-next-page"
                  >
                    Siguiente
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="vehicle-edit-modal">
          <DialogHeader>
            <DialogTitle>Editar Vehículo</DialogTitle>
            <DialogDescription>
              {selectedVehicle?.matricula} - {selectedVehicle?.marca} {selectedVehicle?.modelo}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.operativa}
                  onChange={(e) => setEditForm({ ...editForm, operativa: e.target.checked })}
                  className="rounded border-slate-300"
                  data-testid="vehicle-edit-operativa"
                />
                <span className="text-sm text-slate-700">Operativa</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.favorito}
                  onChange={(e) => setEditForm({ ...editForm, favorito: e.target.checked })}
                  className="rounded border-slate-300"
                  data-testid="vehicle-edit-favorito"
                />
                <span className="text-sm text-slate-700">Favorito</span>
              </label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Capacidad m³</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editForm.capacidad_m3}
                  onChange={(e) => setEditForm({ ...editForm, capacidad_m3: e.target.value })}
                  placeholder="0.0"
                  data-testid="vehicle-edit-capacidad-m3"
                />
              </div>
              <div>
                <Label className="text-xs">Capacidad kg</Label>
                <Input
                  type="number"
                  step="1"
                  value={editForm.capacidad_kg}
                  onChange={(e) => setEditForm({ ...editForm, capacidad_kg: e.target.value })}
                  placeholder="0"
                  data-testid="vehicle-edit-capacidad-kg"
                />
              </div>
              <div>
                <Label className="text-xs">Capacidad tn</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editForm.capacidad_tn}
                  onChange={(e) => setEditForm({ ...editForm, capacidad_tn: e.target.value })}
                  placeholder="0.0"
                  data-testid="vehicle-edit-capacidad-tn"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={3}
                data-testid="vehicle-edit-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} data-testid="vehicle-edit-cancel">
              Cancelar
            </Button>
            <Button onClick={handleUpdateVehicle} className="bg-[#009940] hover:bg-[#007F35]" data-testid="vehicle-edit-save">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="vehicle-import-modal">
          <DialogHeader>
            <DialogTitle>Importar Vehículos desde Excel</DialogTitle>
            <DialogDescription>
              Sube el archivo de maquinaria para importar vehículos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  setImportFile(e.target.files[0]);
                  setImportPreview(null);
                }}
                data-testid="vehicle-import-file-input"
              />
            </div>
            {!importPreview ? (
              <Button onClick={handleImportPreview} disabled={!importFile || importing} className="w-full" data-testid="vehicle-import-preview-button">
                {importing ? 'Procesando...' : 'Vista Previa'}
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <p className="font-medium text-slate-900">Resumen de importación:</p>
                  <ul className="space-y-1 text-slate-600">
                    <li>✅ Nuevos: <span className="font-semibold text-green-700">{importPreview.new}</span></li>
                    <li>⚠️ Ya existen: <span className="font-semibold text-amber-700">{importPreview.existing}</span></li>
                    <li>📊 Total: <span className="font-semibold">{importPreview.total}</span></li>
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleImportCommit('commit_skip')}
                    disabled={importing}
                    className="flex-1"
                    data-testid="vehicle-import-skip-button"
                  >
                    {importing ? 'Importando...' : 'Importar (Omitir Existentes)'}
                  </Button>
                  <Button
                    onClick={() => handleImportCommit('commit_update')}
                    disabled={importing}
                    className="flex-1 bg-[#009940] hover:bg-[#007F35]"
                    data-testid="vehicle-import-update-button"
                  >
                    {importing ? 'Importando...' : 'Importar (Actualizar Existentes)'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
